/** Open source in Task Mantis webapp (citation sidebar). */
export const SOURCE_NAV_MODE_WEBAPP = "webapp";

/** Open URL in tab; optional DOM highlight (Slack, stable permalinks). */
export const SOURCE_NAV_MODE_IN_TAB = "in-tab";

/** Open URL only — no DOM search (Gmail deep link, ephemeral LinkedIn). */
export const SOURCE_NAV_MODE_URL_ONLY = "url-only";

const GMAIL_HOST = "mail.google.com";
const LINKEDIN_HOSTS = new Set(["linkedin.com", "www.linkedin.com"]);

const GMAIL_MESSAGE_HASH_RE =
  /^#(?:inbox|all|sent|starred|important|drafts|spam|trash|label\/[^/]+)\/([A-Za-z0-9_-]+)/i;

/** Gmail thread/message ids used in `#inbox/<id>` URLs (not UUID workflow keys). */
const GMAIL_WEB_MESSAGE_ID_RE = /^[A-Za-z0-9_-]{12,}$/;

/**
 * Hostname from a page URL.
 *
 * @example
 * extractPageHostname("https://app.slack.com/client/T1/C2");
 * // "app.slack.com"
 */
export const extractPageHostname = (pageUrl) => {
  try {
    return new URL(String(pageUrl || "").trim()).hostname.toLowerCase();
  } catch (_) {
    return "";
  }
};

export const isGmailPageUrl = (pageUrl) => extractPageHostname(pageUrl) === GMAIL_HOST;

export const isLinkedInPageUrl = (pageUrl) => LINKEDIN_HOSTS.has(extractPageHostname(pageUrl));

/**
 * Gmail mailbox base without hash (for building `#inbox/<messageId>` links).
 *
 * @example
 * extractGmailMailboxBase("https://mail.google.com/mail/u/1/#inbox");
 * // "https://mail.google.com/mail/u/1"
 */
export const extractGmailMailboxBase = (href) => {
  try {
    const url = new URL(String(href || "").trim() || "https://mail.google.com/mail/u/0/");
    const match = url.pathname.match(/^\/mail\/u\/\d+/);
    const path = match ? match[0] : "/mail/u/0";
    return `${url.origin}${path}`;
  } catch (_) {
    return "https://mail.google.com/mail/u/0";
  }
};

/**
 * True when a Gmail URL already points at a specific thread/message.
 *
 * @example
 * isGmailDirectMessageUrl("https://mail.google.com/mail/u/0/#inbox/FMfcgzGxabc");
 * // true
 */
export const isGmailDirectMessageUrl = (pageUrl) => {
  try {
    const hash = new URL(String(pageUrl || "").trim()).hash || "";
    return GMAIL_MESSAGE_HASH_RE.test(hash);
  } catch (_) {
    return false;
  }
};

/**
 * Build a Gmail web URL that opens one message by id (when id matches Gmail's URL token shape).
 *
 * @example
 * buildGmailMessageUrlFromFileId(
 *   "https://mail.google.com/mail/u/0/#inbox",
 *   "FMfcgzGxYzTqRvgccQQZSBcXqWjQdGH"
 * );
 * // "https://mail.google.com/mail/u/0#inbox/FMfcgzGxYzTqRvgccQQZSBcXqWjQdGH"
 */
export const buildGmailMessageUrlFromFileId = (pageUrl, fileId) => {
  const id = String(fileId || "").trim();
  if (!id || id.startsWith("manual::") || !GMAIL_WEB_MESSAGE_ID_RE.test(id)) return "";
  const base = extractGmailMailboxBase(pageUrl || "https://mail.google.com/mail/u/0/");
  return `${base}#inbox/${id}`;
};

/**
 * Gmail web URL that opens a thread by API thread id.
 *
 * @example
 * buildGmailThreadUrlFromThreadId("https://mail.google.com/mail/u/0/", "18abc");
 * // "https://mail.google.com/mail/u/0#all/18abc"
 */
export const buildGmailThreadUrlFromThreadId = (pageUrl, threadId) => {
  const id = String(threadId || "").trim();
  if (!id) return "";
  const base = extractGmailMailboxBase(pageUrl || "https://mail.google.com/mail/u/0/");
  return `${base}#all/${id}`;
};

/**
 * LinkedIn URLs that survive a reload (post permalinks, messaging threads).
 *
 * @example
 * isLinkedInStablePermalink("https://www.linkedin.com/feed/update/urn:li:activity:123/");
 * // true
 */
export const isLinkedInStablePermalink = (pageUrl) => {
  try {
    const url = new URL(String(pageUrl || "").trim());
    const path = url.pathname.replace(/\/$/, "") || "/";
    if (/^\/feed\/update\//i.test(path)) return true;
    if (/^\/posts\/[^/]+/i.test(path)) return true;
    if (/^\/messaging\/thread\//i.test(path)) return true;
    if (/urn:li:(activity|share|ugcPost):/i.test(url.href)) return true;
    return false;
  } catch (_) {
    return false;
  }
};

/**
 * LinkedIn home feed / profile views where content shifts after navigation.
 *
 * @example
 * isLinkedInEphemeralFeedUrl("https://www.linkedin.com/feed/");
 * // true
 */
export const isLinkedInEphemeralFeedUrl = (pageUrl) => {
  if (!isLinkedInPageUrl(pageUrl)) return false;
  if (isLinkedInStablePermalink(pageUrl)) return false;
  try {
    const path = new URL(String(pageUrl || "").trim()).pathname.replace(/\/$/, "") || "/";
    if (path === "/feed") return true;
    if (/^\/in\/[^/]+$/i.test(path)) return true;
    if (/^\/notifications/i.test(path)) return true;
    return true;
  } catch (_) {
    return true;
  }
};

/**
 * Normalize URLs for “already on this page” checks (ignore hash and trailing slash).
 *
 * @example
 * normalizeUrlForTabReuse("https://www.linkedin.com/feed/update/urn:li:activity:1/?x=1");
 * // "https://www.linkedin.com/feed/update/urn:li:activity:1?x=1"
 */
export const normalizeUrlForTabReuse = (href) => {
  try {
    const url = new URL(String(href || "").trim());
    const path = url.pathname.replace(/\/$/, "") || "/";
    return `${url.origin}${path}${url.search}`;
  } catch (_) {
    return "";
  }
};

/**
 * Decide how the extension should open “See original” for a row.
 *
 * @example
 * buildSourceNavigationPlan({
 *   pageUrl: "https://mail.google.com/mail/u/0/",
 *   threadSubject: "Q2 follow-up",
 *   webappFallbackUrl: "https://app.taskmantis.com/business/action-items?...",
 * });
 * // { mode: "webapp", webappUrl: "https://app.taskmantis.com/..." }
 *
 * @example
 * buildSourceNavigationPlan({
 *   pageUrl: "https://app.slack.com/client/T1/C2",
 *   sourceSnippet: "Ship the deck",
 * });
 * // { mode: "in-tab", destUrl: "...", tryDomHighlight: true, domHighlightAttempts: 1 }
 */
export const buildSourceNavigationPlan = ({
  pageUrl = "",
  sourceSnippet = "",
  threadSubject = "",
  threadId = "",
  webappFallbackUrl = "",
  fileId = "",
  isManual = false,
} = {}) => {
  const normalizedPageUrl = String(pageUrl || "").trim();
  const normalizedSnippet = String(sourceSnippet || "").trim();
  const normalizedThreadSubject = String(threadSubject || "").trim();
  const normalizedFallbackUrl = String(webappFallbackUrl || "").trim();
  const normalizedFileId = String(fileId || "").trim();
  const normalizedThreadId = String(threadId || "").trim();
  const onGmail =
    isGmailPageUrl(normalizedPageUrl) || Boolean(normalizedThreadSubject);

  if (onGmail) {
    const directFromStoredUrl = isGmailDirectMessageUrl(normalizedPageUrl)
      ? normalizedPageUrl
      : "";
    const directFromFileId =
      !isManual && normalizedFileId
        ? buildGmailMessageUrlFromFileId(normalizedPageUrl, normalizedFileId)
        : "";
    const directFromThreadId = normalizedThreadId
      ? buildGmailThreadUrlFromThreadId(normalizedPageUrl, normalizedThreadId)
      : "";
    const gmailDirectUrl = directFromStoredUrl || directFromFileId || directFromThreadId;

    if (gmailDirectUrl) {
      return {
        mode: SOURCE_NAV_MODE_URL_ONLY,
        destUrl: gmailDirectUrl,
        tryDomHighlight: Boolean(normalizedSnippet),
        domHighlightAttempts: 8,
        domHighlightRetryMs: 1200,
        settleMs: 2500,
        openWebappOnHighlightMiss: true,
        webappFallbackPauseMs: 5000,
        toastOnSuccess: normalizedSnippet
          ? "Highlighted task text in this email ✓"
          : "Opened message in Gmail",
        toastOnFindBar:
          "Opened browser Find (Ctrl+F) with your task text — press Enter for the next match.",
        toastOnHighlightMiss:
          "Could not auto-highlight the task in this Gmail thread (the saved text may differ slightly from the email). Opening the full source citation in Task Mantis…",
        toastOnWebappOpened:
          "Task Mantis source view — full email text and search highlights for this task are here.",
      };
    }

    if (normalizedFallbackUrl) {
      return {
        mode: SOURCE_NAV_MODE_WEBAPP,
        webappUrl: normalizedFallbackUrl,
        toastOnSuccess: "Opening source in Task Mantis…",
      };
    }

    if (normalizedPageUrl) {
      return {
        mode: SOURCE_NAV_MODE_URL_ONLY,
        destUrl: normalizedPageUrl,
        tryDomHighlight: false,
        toastOnSuccess: "Opened Gmail — use Task Mantis for full source",
      };
    }
  }

  if (isLinkedInPageUrl(normalizedPageUrl)) {
    if (isLinkedInEphemeralFeedUrl(normalizedPageUrl)) {
      if (normalizedFallbackUrl) {
        return {
          mode: SOURCE_NAV_MODE_WEBAPP,
          webappUrl: normalizedFallbackUrl,
          toastOnSuccess: "Opening capture in Task Mantis (LinkedIn feed refreshes)",
        };
      }
      return {
        mode: SOURCE_NAV_MODE_URL_ONLY,
        destUrl: normalizedPageUrl,
        tryDomHighlight: false,
        toastOnSuccess:
          "Opened LinkedIn — the feed may have changed since you captured this",
      };
    }

    return {
      mode: SOURCE_NAV_MODE_IN_TAB,
      destUrl: normalizedPageUrl,
      tryDomHighlight: Boolean(normalizedSnippet),
      domHighlightAttempts: 3,
      domHighlightRetryMs: 700,
      settleMs: 2200,
      reuseTabIfSameUrl: true,
      webappFallbackUrl: normalizedFallbackUrl,
      toastOnSuccess: "Found in page ✓",
    };
  }

  if (!normalizedPageUrl && normalizedThreadSubject) {
    if (normalizedFallbackUrl) {
      return {
        mode: SOURCE_NAV_MODE_WEBAPP,
        webappUrl: normalizedFallbackUrl,
      };
    }
    return { mode: SOURCE_NAV_MODE_WEBAPP, webappUrl: "", error: "No source URL available" };
  }

  if (!normalizedPageUrl) {
    if (normalizedFallbackUrl) {
      return { mode: SOURCE_NAV_MODE_WEBAPP, webappUrl: normalizedFallbackUrl };
    }
    return { mode: SOURCE_NAV_MODE_WEBAPP, webappUrl: "", error: "No source URL available" };
  }

  return {
    mode: SOURCE_NAV_MODE_IN_TAB,
    destUrl: normalizedPageUrl,
    tryDomHighlight: Boolean(normalizedSnippet),
    domHighlightAttempts: 1,
    domHighlightRetryMs: 0,
    settleMs: 800,
    reuseTabIfSameUrl: false,
    webappFallbackUrl: normalizedFallbackUrl,
    toastOnSuccess: "Found in page ✓",
  };
};
